import React from 'react'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { MaterialValueEditor } from '@react-querybuilder/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ValueEditorProps } from 'react-querybuilder'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/fr'
import { Box } from '@mui/material'

dayjs.extend(utc)

const omit = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> =>
  Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key as K))) as Omit<T, K>

const initDayjs = (v: string | Dayjs | null) => (v ? dayjs.utc(v as string).local() : null)

export const CustomDateEditor: React.FC<ValueEditorProps> = (props) => {
  const { fieldData, operator, value, handleOnChange } = props

  const isRangeOperator = operator === 'between' || operator === 'notBetween'
  const isDateType = fieldData.inputType === 'date'
  const isDateTimeType = fieldData.inputType === 'datetime-local'

  if (isDateType || isDateTimeType) {
    const PickerComponent = isDateType ? DatePicker : DateTimePicker

    const values: (Dayjs | null)[] = isRangeOperator
      ? Array.isArray(value)
        ? value.map(initDayjs)
        : typeof value === 'string' && value.includes(',')
        ? value.split(',').map(initDayjs)
        : [null, null]
      : [initDayjs(value as string | Dayjs | null)]

    const handleChangeAndConvert = (newVal: Dayjs | null, otherVal: Dayjs | null = null, index: 1 | 2 | 0) => {
      if (!newVal) {
        if (isRangeOperator) {
          const rangeValues = index === 1 ? [null, otherVal] : [otherVal, null]
          const convertedValues = rangeValues.map((v) => (v ? v.utc().toISOString() : null))
          handleOnChange(convertedValues.join(','))
        } else {
          handleOnChange(null)
        }
        return
      }

      let utcString = newVal.utc().toISOString()
      if (isDateType) utcString = newVal.utc().startOf('day').toISOString()

      if (isRangeOperator) {
        const currentRange =
          index === 1
            ? [utcString, otherVal ? otherVal.utc().toISOString() : null]
            : [otherVal ? otherVal.utc().toISOString() : null, utcString]
        handleOnChange(currentRange.join(','))
      } else {
        handleOnChange(utcString)
      }
    }

    if (isRangeOperator) {
      const [val1, val2] = values
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='fr'>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <PickerComponent
              value={val1}
              sx={{ backgroundColor: 'white' }}
              slotProps={{ textField: { size: 'small', error: false } }}
              onChange={(newVal) => handleChangeAndConvert(newVal ?? null, val2, 1)}
            />
            <PickerComponent
              value={val2}
              sx={{ backgroundColor: 'white' }}
              slotProps={{ textField: { size: 'small', error: false } }}
              onChange={(newVal) => handleChangeAndConvert(newVal ?? null, val1, 2)}
            />
          </Box>
        </LocalizationProvider>
      )
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='fr'>
        <PickerComponent
          value={values[0]}
          sx={{ backgroundColor: 'white' }}
          slotProps={{ textField: { size: 'small', error: false } }}
          onChange={(newVal) => handleChangeAndConvert(newVal ?? null, null, 0)}
        />
      </LocalizationProvider>
    )
  }

  const safeProps = omit(props, ['parseNumbers'])
  return <MaterialValueEditor {...safeProps} />
}
